import { liteClient as algoliasearch } from 'algoliasearch/lite'
import instantsearch from 'instantsearch.js'
import { configure, index } from 'instantsearch.js/es/widgets'
import { connectInfiniteHits, connectSearchBox, connectMenu } from 'instantsearch.js/es/connectors'

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

const truncate = (value, maxLength = 220) => {
  if (!value || value.length <= maxLength) {
    return value ?? ''
  }

  return `${value.slice(0, maxLength - 1).trimEnd()}...`
}

// Resolve a Craft section handle to a display label. `labels` is the translated
// handle => label map from the template; a missing handle falls back to 'Page' and handles
// not in the map fall back to a humanized camelCase handle so new sections still render.
const sectionLabel = (sectionHandle, labels = {}) => {
  if (typeof sectionHandle !== 'string' || sectionHandle.trim() === '') {
    return 'Page'
  }

  if (Object.hasOwn(labels, sectionHandle) && labels[sectionHandle]) {
    return labels[sectionHandle]
  }

  const withSpaces = sectionHandle.replace(/([a-z])([A-Z])/g, '$1 $2')
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
}

const HITS_PER_PAGE = 10

// results._state (private to algoliasearch-helper) went missing: logged once per page, not per render.
let stateWarned = false

// Brands hidden from every other site's federated results (each still sees itself).
// Entries must match each brand's BRAND_HANDLE exactly — the filter interpolates these verbatim
// and Algolia's facet match is case-sensitive, so a wrong-case entry silently suppresses nothing.
export const SUPPRESSED_FEDERATED_BRAND_HANDLES = ['crmengineering', 'golden', 'texwrapfabricators']

// ---------------------------------------------------------------------------
// Alpine component
//
// Runs a single instantsearch instance with two indices in parallel:
//   - Entries (root):    `entriesIndexName`   — per-business-line, scoped to this brand + locale
//   - Federated (child): `federatedIndexName` — cross-brand, filtered by language / businessLine
//
// `searchMode` is a UI scope, not an index: 'local' means "this site's own results" and reads
// the entries index; 'businessLine' and 'allPromach' read the federated one. Switching modes
// swaps the relevant configure widget; both indices keep running so query state is preserved.
// ---------------------------------------------------------------------------

export default (config = {}) => ({
  ...config,

  // normalization / aliasing — overrides spread values where needed
  currentBrandHandle: String(config.currentBrandHandle ?? ''),
  currentSiteId: Number.parseInt(String(config.currentSiteId ?? ''), 10),
  initialQuery: config.query ?? '',
  sectionLabels: config.sectionLabels ?? {},

  // internal state — not from config
  refineSearch: null,        // bound by connectSearchBox — call to update query
  search: null,              // instantsearch instance, set in init()
  searchClient: null,        // Algolia lite client, set in init()
  queryText: '',
  hasQuery: false,
  hasConfigError: false,

  // --- Search scope ---
  // 'local' | 'businessLine' | 'allPromach'
  searchMode: 'local',
  activeSection: '',         // persists across mode switches; cleared if the target mode lacks it

  // Unfiltered section facet items per scope; null = never loaded. Filled from the page queries when
  // no section is applied, or by _loadSectionItems() when a section is kept across a scope switch
  // (the filtered query only returns that one section). A query change leaves the items in place
  // (so the dropdown doesn't blink out while typing) but stale: see sectionQueryByScope.
  sectionItemsByScope: { local: null, businessLine: null, allPromach: null },
  sectionQueryByScope: { local: null, businessLine: null, allPromach: null }, // query each scope's items are for
  sectionLoads: {},          // in-flight _loadSectionItems() requests, keyed by scope + query
  scopeSwitchSeq: 0,         // bumped on every setMode(); lets a held-back switch detect it was superseded
  scopeHeld: false,          // active scope's results are held back while its section counts load

  // --- Local index state ---
  entriesConfigureWidget: null,
  entriesFiltersBase: '',      // brand + locale filter, built once in init()
  entriesLoading: true,        // true until first search result arrives
  entriesHits: [],
  entriesNbHits: 0,
  entriesCanShowMore: false,
  entriesShowMore: null,       // showMore fn from connectInfiniteHits

  // --- Federated index state ---
  federatedConfigureWidget: null,
  federatedIndexWidget: null,
  federatedLoading: false,   // true between a mode/section switch and the first result callback
  federatedHits: [],
  federatedNbHits: 0,
  federatedCanShowMore: false,
  federatedShowMore: null,   // showMore fn from connectInfiniteHits

  // ---------------------------------------------------------------------------
  // Computed getters — expose active-mode values so the template doesn't need
  // to repeat mode ternaries on every binding.
  // ---------------------------------------------------------------------------

  get isLocalSearch()     { return this.searchMode === 'local' },
  get activeHits()        { return this.isLocalSearch ? this.entriesHits         : this.federatedHits        },
  get activeNbHits()      { return this.isLocalSearch ? this.entriesNbHits       : this.federatedNbHits      },
  get activeSectionItems() {
    const items = this.sectionItemsByScope[this.searchMode] ?? []
    // Counts for this scope are loading or stale and lack the applied section: keep it selectable
    // (count 0, which the template hides). Otherwise the select would show "All Content Types"
    // while the results are still filtered by it.
    if (!this.activeSection || items.some(item => item.value === this.activeSection)) return items
    return this._toSectionItems([...items, { value: this.activeSection, count: 0 }])
  },
  get activeCanShowMore() { return this.isLocalSearch ? this.entriesCanShowMore  : this.federatedCanShowMore  },

  // True when the active mode has finished loading and has zero results.
  get hasNoActiveResults() {
    return this.isLocalSearch
      ? !this.entriesLoading && this.entriesNbHits === 0
      : !this.federatedLoading && this.federatedNbHits === 0
  },

  // Adds translated labels to the section facet items and sorts by label, since
  // Algolia sorts by handle, which no longer matches the displayed order.
  _toSectionItems(items) {
    return items
      .map(item => ({ ...item, label: sectionLabel(item.value, this.sectionLabels) }))
      .sort((a, b) => a.label.localeCompare(b.label, this.currentLanguage || undefined))
  },

  // ---------------------------------------------------------------------------
  // Filter builder
  // ---------------------------------------------------------------------------

  // Builds the Algolia filter string for the federated index for a mode and section.
  _federatedFiltersFor(mode, section = '') {
    const parts = []
    if (mode === 'businessLine' && this.currentBusinessLineHandle) {
      parts.push(`businessLineHandle:"${this.currentBusinessLineHandle}"`)
    }
    if (this.currentLanguage) parts.push(`language:"${this.currentLanguage}"`)
    if (section)              parts.push(`section:"${section}"`)
    // Case-insensitive: the list is lowercase but the handle is verbatim, so a camelCase brand
    // would otherwise fail to match itself and suppress its own results.
    SUPPRESSED_FEDERATED_BRAND_HANDLES
      .filter(handle => handle.toLowerCase() !== this.currentBrandHandle.toLowerCase())
      .forEach(handle => parts.push(`NOT brandHandle:"${handle}"`))
    return parts.join(' AND ')
  },

  // Filter string for the federated index in the current mode and active section.
  _buildFederatedFilters() {
    return this._federatedFiltersFor(this.searchMode, this.activeSection)
  },

  // ---------------------------------------------------------------------------
  // Section facet cache
  // ---------------------------------------------------------------------------

  // Scopes whose unfiltered facets a menu response carries. Matching on the filters the response
  // was actually queried with drops late or section-filtered responses, which would otherwise
  // be filed under the wrong scope or replace the options with the single applied section.
  //
  // This reads `results._state`, which is private to algoliasearch-helper: the public connector
  // state doesn't say which filters a response was queried with. If an InstantSearch upgrade drops
  // it nothing can be filed (guessing a scope is how the stale-counts bug comes back), so log it
  // and file nothing; federatedSearch.contract.test.js fails first in CI.
  _scopesForResults(isEntriesIndex, results) {
    if (!results) return []
    const state = results._state
    if (!state) {
      if (!stateWarned) {
        stateWarned = true
        console.error('Federated search: results._state is missing, section counts cannot be filed.')
      }
      return []
    }
    // results.query, not state.query: child indices (federated) don't carry the query in their state.
    if ((results.query ?? '').trim() !== this.queryText) return []
    const filters = state.filters ?? ''
    if (isEntriesIndex) return filters === this.entriesFiltersBase ? ['local'] : []
    return ['businessLine', 'allPromach'].filter(scope => filters === this._federatedFiltersFor(scope))
  },

  // connectMenu params for the section facet. connectMenu only passes `results` (the search state a
  // response was queried with) to transformItems, not to its render callback, so items are filed
  // there and the render callback is a no-op. `queryText` lags a new query's response by one render
  // pass, so that first pass drops the facets (query mismatch) and a later pass refiles them; the
  // typing case is covered by federatedSearch.contract.test.js.
  _sectionMenuParams(isEntriesIndex) {
    return {
      attribute: 'section',
      limit: 50,
      transformItems: (items, { results }) => {
        this._storeSectionItems(this._scopesForResults(isEntriesIndex, results), this._toSectionItems(items))
        return items
      },
    }
  },

  _storeSectionItems(scopes, items) {
    if (scopes.length === 0) return
    const nextItems = { ...this.sectionItemsByScope }
    const nextQueries = { ...this.sectionQueryByScope }
    scopes.forEach((scope) => {
      nextItems[scope] = items
      nextQueries[scope] = this.queryText
    })
    this.sectionItemsByScope = nextItems
    this.sectionQueryByScope = nextQueries
  },

  // Items for a scope only if they belong to the current query, else null.
  _freshSectionItems(scope) {
    return this.sectionQueryByScope[scope] === this.queryText ? this.sectionItemsByScope[scope] : null
  },

  // Unfiltered section counts for a scope, fetched with one facet-only request. The caller checks
  // _freshSectionItems() first and only calls this when there is nothing fresh. Needed when a scope
  // switch keeps a section applied, since the filtered page query only returns that one section.
  // Concurrent calls for the same scope and query share one request. Resolves to the items, or
  // null on failure or if the query changed meanwhile.
  _loadSectionItems(scope) {
    const query = this.queryText
    const key = `${scope}:${query}`
    if (this.sectionLoads[key]) return this.sectionLoads[key]

    const load = (async () => {
      try {
        const filters = scope === 'local' ? this.entriesFiltersBase : this._federatedFiltersFor(scope)
        const { results } = await this.searchClient.search({
          requests: [{
            indexName: scope === 'local' ? this.entriesIndexName : this.federatedIndexName,
            query,
            ...(filters ? { filters } : {}),
            facets: ['section'],
            maxValuesPerFacet: 50,
            hitsPerPage: 0,
            analytics: false, // bookkeeping query, not a user search
          }],
        })
        const items = this._toSectionItems(
          Object.entries(results?.[0]?.facets?.section ?? {})
            .map(([value, count]) => ({ value, count, isRefined: false })),
        )
        // The query changed in flight: this result belongs to the old one.
        if (query !== this.queryText) return null
        this._storeSectionItems([scope], items)
        return items
      } catch (error) {
        console.error('Federated search: could not load section counts.', error)
        return null
      }
    })()

    // Cleaned up on the stored promise, not in a finally inside the async body: that would run
    // before this assignment if the client threw synchronously and leave a settled entry behind.
    this.sectionLoads[key] = load
    load.finally(() => {
      if (this.sectionLoads[key] === load) delete this.sectionLoads[key]
    })
    return load
  },

  // ---------------------------------------------------------------------------
  // Public API — called from the template
  // ---------------------------------------------------------------------------

  // Advance to the next page of results for the active mode.
  loadMore() {
    if (this.isLocalSearch) {
      this.entriesShowMore?.()
    } else {
      this.federatedShowMore?.()
    }
  },

  // Filter results by content type. Persists across mode switches.
  setSection(value) {
    if (!this.search || !this.entriesConfigureWidget) return
    this.activeSection = value
    this._refreshScope(this.searchMode)
  },

  // Switch between 'local', 'businessLine', and 'allPromach' search scopes.
  // Keeps the active section filter unless the target scope doesn't expose that section
  // (the local and federated indices expose different section sets). When that scope's counts
  // aren't known for the current query, the results are held back (loading state) until they
  // arrive, so a section the scope lacks never costs a filtered query that is thrown away.
  setMode(mode) {
    if (!this.search || !this.entriesConfigureWidget || !this.federatedConfigureWidget) return
    if (mode === this.searchMode) return
    this.searchMode = mode
    const seq = ++this.scopeSwitchSeq

    const items = this._freshSectionItems(mode)
    if (this.activeSection && items === null) {
      this._resetResults(mode === 'local')
      this.scopeHeld = true
      this._loadSectionItems(mode).then((loaded) => {
        // Superseded by another switch, or the user changed the section or query while counts
        // loaded. Both end in a refresh, which releases the hold, so there is nothing left to do.
        if (!this.scopeHeld || seq !== this.scopeSwitchSeq) return
        // Only a successful load can say the scope lacks the section. On failure (loaded === null)
        // keep the user's choice rather than drop it on a guess. The counts refill on the next
        // switch, or when the section is cleared or the query changes.
        if (loaded && !loaded.some(item => item.value === this.activeSection)) this.activeSection = ''
        this._refreshScope(mode)
      }).catch((error) => {
        // Never leave the scope held back (blank, loading) if the refresh itself throws.
        this.scopeHeld = false
        console.error('Federated search: could not switch search scope.', error)
      })
      return
    }

    if (this.activeSection && !items.some(item => item.value === this.activeSection)) {
      this.activeSection = ''
    }
    this._refreshScope(mode)
  },

  // ---------------------------------------------------------------------------
  // Private helpers — configure widget swap pattern
  //
  // Both indices use the same approach: reset result state, reset pagination,
  // swap the configure widget (which triggers a new Algolia query), and update
  // the stored widget ref for the next swap.
  // ---------------------------------------------------------------------------

  _resetResults(isLocal) {
    if (isLocal) {
      this.entriesHits = []
      this.entriesNbHits = 0
      this.entriesCanShowMore = false
      this.entriesLoading = true
    } else {
      this.federatedHits = []
      this.federatedNbHits = 0
      this.federatedCanShowMore = false
      this.federatedLoading = true
    }
  },

  _refreshScope(mode) {
    if (mode === 'local') {
      this._refreshEntriesIndex()
    } else {
      this._refreshFederatedIndex()
    }
  },

  _refreshEntriesIndex() {
    this.scopeHeld = false
    this._resetResults(true)
    this.search.helper?.setPage(0)

    // entriesFiltersBase is always set — init() bails without a brand and site — so the brand
    // clause can never drop out here.
    const filterParts = [this.entriesFiltersBase]
    if (this.activeSection) filterParts.push(`section:"${this.activeSection}"`)

    const oldWidget = this.entriesConfigureWidget
    this.entriesConfigureWidget = configure({
      hitsPerPage: HITS_PER_PAGE,
      filters: filterParts.join(' AND '),
    })
    this.search.removeWidgets([oldWidget])
    this.search.addWidgets([this.entriesConfigureWidget])
  },

  _refreshFederatedIndex() {
    this.scopeHeld = false
    this._resetResults(false)
    // Reset the child index helper's page — filter changes don't auto-reset it.
    this.federatedIndexWidget.getHelper?.()?.setPage(0)

    const filters = this._buildFederatedFilters()
    const oldWidget = this.federatedConfigureWidget
    this.federatedConfigureWidget = configure({
      hitsPerPage: HITS_PER_PAGE,
      ...(filters ? { filters } : {}),
    })
    this.federatedIndexWidget.removeWidgets([oldWidget])
    this.federatedIndexWidget.addWidgets([this.federatedConfigureWidget])
  },

  _initFederatedIndex() {
    const federatedIndex = index({ indexName: this.federatedIndexName })
    this.federatedIndexWidget = federatedIndex
    const initialFederatedFilters = this._buildFederatedFilters()
    this.federatedConfigureWidget = configure({
      hitsPerPage: HITS_PER_PAGE,
      ...(initialFederatedFilters ? { filters: initialFederatedFilters } : {}),
    })
    federatedIndex.addWidgets([
      this.federatedConfigureWidget,
      // Section facets — read-only; cached per scope from responses that carry no section filter.
      connectMenu(() => {})(this._sectionMenuParams(false)),
      connectInfiniteHits(({ hits, showMore, isLastPage, results }) => {
        // A response for the scope we switched away from would repaint the held-back list.
        if (this.scopeHeld && !this.isLocalSearch) return
        this.federatedNbHits = results?.nbHits ?? 0
        this.federatedHits = hits.map(hit => ({
          ...hit,
          excerpt: truncate(hit.description || hit._snippetResult?.content?.value || hit.content || '', 108),
          sectionLabel: sectionLabel(hit.section, this.sectionLabels),
          highlightedTitle: hit._highlightResult?.title?.value ?? hit.title,
          brandLabel: hit.brandName || hit.brandHandle || '',
          brandLogoUrl: hit.brandLogoUrl ?? null,
          brandHeadline: hit.brandHeadline ?? null,
          isCurrentSite: hit.brandHandle === this.currentBrandHandle,
        }))
        this.federatedCanShowMore = !isLastPage
        this.federatedShowMore = showMore
        this.federatedLoading = false
      })({ escapeHTML: false }),
    ])
    return federatedIndex
  },

  _initEntriesWidgets(federatedIndex) {
    this.entriesFiltersBase = `brandHandle:"${this.currentBrandHandle}" AND siteId:${this.currentSiteId}`
    this.entriesConfigureWidget = configure({
      hitsPerPage: HITS_PER_PAGE,
      filters: this.entriesFiltersBase,
    })

    return [
      connectSearchBox(({ query: searchQuery, refine }) => {
        const newQuery = searchQuery.trim()
        // Clear section filter when the query changes — stale section + new query = confusing.
        // Cached section items stay (see sectionQueryByScope) but no longer count as fresh.
        if (newQuery !== this.queryText && this.activeSection !== '') {
          this.setSection('')
        }
        this.queryText = newQuery
        this.hasQuery = newQuery.length > 0
        this.refineSearch = refine
      })({}),
      // Section facets — read-only; section filter is applied via configure swap
      // in setSection() rather than refine() to isolate from child indices.
      connectMenu(() => {})(this._sectionMenuParams(true)),
      this.entriesConfigureWidget,
      connectInfiniteHits(({ hits, showMore, isLastPage, results }) => {
        if (this.scopeHeld && this.isLocalSearch) return
        this.entriesNbHits = results?.nbHits ?? 0

        const mapHit = hit => ({
          ...hit,
          imageUrl: hit.imageUrl ?? this.fallbackImageUrl,
          excerpt: truncate(hit._snippetResult?.content?.value || hit.description || hit.content || ''),
          sectionLabel: sectionLabel(hit.section, this.sectionLabels),
          highlightedTitle: hit._highlightResult?.title?.value ?? hit.title,
          isCurrentSite: hit.brandHandle === this.currentBrandHandle,
        })

        // On page 0 (fresh query or filter change), replace hits. On subsequent
        // pages (load more), append — deduping by objectID as a safety net.
        if ((results?.page ?? 0) === 0) {
          this.entriesHits = hits.map(mapHit)
        } else {
          const existingIds = new Set(this.entriesHits.map(h => h.objectID))
          const newHits = hits.filter(h => !existingIds.has(h.objectID))
          this.entriesHits = [...this.entriesHits, ...newHits.map(mapHit)]
        }

        this.entriesCanShowMore = !isLastPage
        this.entriesShowMore = showMore
        this.entriesLoading = false
      })({ escapeHTML: false }),
      federatedIndex,
    ]
  },

  // ---------------------------------------------------------------------------
  // Init — wires up the instantsearch instance
  // ---------------------------------------------------------------------------

  init() {
    if (!this.appId || !this.searchApiKey || !this.entriesIndexName || !this.federatedIndexName) {
      this.hasConfigError = true
      return
    }

    if (!Number.isInteger(this.currentSiteId) || this.currentSiteId < 1) {
      this.hasConfigError = true
      console.error('Federated search config error: currentSiteId is missing or invalid.', {
        currentSiteId: this.currentSiteId,
      })
      return
    }

    if (!this.currentBrandHandle) {
      this.hasConfigError = true
      return
    }

    this.searchClient = algoliasearch(this.appId, this.searchApiKey)
    const urlQuery = new URLSearchParams(window.location.search).get('q') ?? ''
    const resolvedQuery = this.initialQuery || urlQuery
    this.queryText = resolvedQuery.trim()
    this.hasQuery = this.queryText.length > 0

    this.search = instantsearch({
      indexName: this.entriesIndexName,
      searchClient: this.searchClient,
      initialUiState: {
        [this.entriesIndexName]: { query: resolvedQuery },
      },
    })
    this.search.on('error', () => {
      this.hasConfigError = true
    })

    const federatedIndex = this._initFederatedIndex()
    this.search.addWidgets(this._initEntriesWidgets(federatedIndex))
    this.search.start()
  },

})
